<template>
  <fieldset class="quote-header-fieldset"
    :disabled="$licenceExpired">
    <div class="quote-header fixed-grid has-4-cols-desktop has-1-cols-mobile">
      <div class="grid">

        <quote-vehicle v-if="innerVehicle"
          v-model="innerVehicle"
          :quote.sync="innerValue"
          :is-saving="headerSavingPanels.vehicle"
          @updateQuoteItemsDefaultPaintGroup="(newVal, oldVal) => $emit('updateQuoteItemsDefaultPaintGroup', newVal, oldVal)"
          @select-vehicle="selectVehicle"
          @component-blur="$emit('vehicle-component-blur')"
          :insurer="innerInsurer" />
        <quote-customer v-if="innerCustomer"
          v-model="innerCustomer"
          :quote.sync="innerValue"
          :is-saving="headerSavingPanels.customer"
          :is-new-customer-prompt="isNewCustomerPrompt"
          @select-customer="selectCustomer"
          @component-blur="$emit('customer-component-blur')" />
        <quote-insurer v-if="innerInsurer"
          v-model="innerInsurer"
          :quote.sync="innerValue"
          :is-saving="headerSavingPanels.insurer"
          :vehicle="innerVehicle"
          @select-insurer="selectInsurer"
          @component-blur="$emit('insurer-component-blur')"
          :next-line-number="nextLineNumber" />
        <quote-booking
          v-model="innerQuoteCycleTime"
          :is-saving="headerSavingPanels.booking"
          :quote.sync="innerValue"
          :insurer="innerInsurer"
          @component-blur="$emit('booking-component-blur')" />
      </div>
    </div>
  </fieldset>
</template>

<script>
import _cloneDeep from 'lodash.clonedeep'
import { QuoteVehicle, QuoteCustomer, QuoteInsurer, QuoteBooking } from './components/QuoteHeader'
import storeMixin from './storeMixin'
import { EventHubTypes } from '@/enums'
import DeepDiif from 'deep-diff'

export default {
  name: 'QuoteHeader',
  inject: ['$vv'],
  components: { QuoteVehicle, QuoteCustomer, QuoteInsurer, QuoteBooking },
  mixins: [storeMixin],
  props: {
    value: null,
    vehicle: null,
    customer: null,
    insurer: null,
    quoteCycleTime: null,
    nextLineNumber: null,
    isNewCustomerPrompt: false,
    headerSavingPanels: {
      type: Object,
      default: () => ({ vehicle: false, customer: false, insurer: false, booking: false })
    }
  },
  data: () => {
    return {
      innerValue: null,
      innerVehicle: null,
      innerCustomer: null,
      innerInsurer: null,
      innerQuoteCycleTime: null,
      isPauseWatch: false,
      prevInnerValue: null,
      prevInnerVehicle: null,
      prevInnerCustomer: null,
      prevInnerInsurer: null,
      prevInnerQuoteCycleTime: null
    }
  },
  computed: {
    $v() {
      return this.$vv || this.$v
    }
  },
  watch: {
    innerValue: {
      handler: function (newVal, oldVal) {
        const diff = DeepDiif.diff(this.prevInnerValue, newVal)
        if (!this.isPauseWatch && diff) {
          this.prevInnerValue = _cloneDeep(newVal)
          this.$emit('input', newVal)
          this.$nextTick(() => {
            this.$eventHub.$emit(`${EventHubTypes.EntitySaved}-${this.$options.name}`)
          })
        }
      },
      deep: true
    },
    innerVehicle: {
      handler: function (newVal, oldVal) {
        const diff = DeepDiif.diff(this.prevInnerVehicle, newVal)
        if (!this.isPauseWatch && diff) {
          this.prevInnerVehicle = _cloneDeep(newVal)
          this.$emit('update:vehicle', newVal)
        }
      },
      deep: true
    },
    innerCustomer: {
      handler: function (newVal, oldVal) {
        const diff = DeepDiif.diff(this.prevInnerCustomer, newVal)
        if (!this.isPauseWatch && diff) {
          this.prevInnerCustomer = _cloneDeep(newVal)
          this.$emit('update:customer', newVal)
        }
      },
      deep: true
    },
    innerInsurer: {
      handler: function (newVal, oldVal) {
        const diff = DeepDiif.diff(this.prevInnerInsurer, newVal)
        if (!this.isPauseWatch && diff) {
          this.prevInnerInsurer = _cloneDeep(newVal)
          this.$emit('update:insurer', newVal)
        }
      },
      deep: true
    },
    innerQuoteCycleTime: {
      handler: function (newVal, oldVal) {
        const diff = DeepDiif.diff(this.prevInnerQuoteCycleTime, newVal)
        if (!this.isPauseWatch && diff) {
          this.prevInnerQuoteCycleTime = _cloneDeep(newVal)
          this.$emit('update:quoteCycleTime', newVal)
        }
      },
      deep: true
    },
    value: {
      handler: function (newVal, oldVal) {
        if (!this.isPauseWatch) {
          this.innerValue = _cloneDeep(newVal)
        }
      },
      deep: true
    }
  },
  created() {
    this.isPauseWatch = true
    this.innerValue = _cloneDeep(this.value)
    this.innerVehicle = _cloneDeep(this.vehicle)
    this.innerCustomer = _cloneDeep(this.customer)
    this.innerInsurer = _cloneDeep(this.insurer)
    this.innerQuoteCycleTime = _cloneDeep(this.value.quoteCycleTime)
    this.isPauseWatch = false
  },
  mounted() {
    this.$eventHub.$on(EventHubTypes.EntitySaved, this.updateInnerObjects)
    // this.$vv.quoteHeaderGroup.$touch()
  },
  beforeDestroy() {
    this.$eventHub.$off(EventHubTypes.EntitySaved)
  },
  methods: {
    updateInnerObjects() {
      this.isPauseWatch = true
      this.innerValue = _cloneDeep(this.value)
      this.innerVehicle = _cloneDeep(this.vehicle)
      this.innerCustomer = _cloneDeep(this.customer)
      this.innerInsurer = _cloneDeep(this.insurer)
      this.innerQuoteCycleTime = _cloneDeep(this.value.quoteCycleTime)
      this.isPauseWatch = false
      this.$nextTick(() => {
        this.$eventHub.$emit(`${EventHubTypes.EntitySaved}-${this.$options.name}`)
      })
    },
    selectVehicle(vehicleId, customerId) {
      this.$emit('select-vehicle', vehicleId, customerId)
    },
    selectInsurer(id) {
      this.$emit('select-insurer', id)
    },
    selectCustomer(id) {
      this.$emit('select-customer', id)
    }
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteLeave(to, from, next) {
    next()
  }
}
</script>

<style lang="scss" scoped>
.quote-header-fieldset,
.quote-header.fixed-grid,
.quote-header.fixed-grid > .grid {
  height: 100%;
}

.quote-header-fieldset {
  border: 0;
  min-width: 0;

  &:disabled {
    ::v-deep a.button,
    ::v-deep .cursor-pointer {
      pointer-events: none !important;
    }

    ::v-deep .control:not(:has(.control)):hover::before {
      content: 'Disabled, as your licence has expired';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translate(-50%, 0.5rem);
      z-index: 30;
      padding: 0.4rem 0.8rem;
      border-radius: 4px;
      background: rgba(74, 74, 74, 0.9);
      color: #fff;
      font-size: 0.75rem;
      white-space: nowrap;
      pointer-events: none;
    }

    ::v-deep .grid > :first-child .control:not(:has(.control)):hover::before {
      left: 0;
      transform: translate(0, 0.5rem);
    }

    ::v-deep .grid > :last-child .control:not(:has(.control)):hover::before {
      left: auto;
      right: 0;
      transform: translate(0, 0.5rem);
    }
  }
}
</style>

<style lang="scss">
.quote-item-saving-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: all;
  overflow: hidden;
  border-radius: var(--bulma-radius-small, 4px);
  background: color-mix(in srgb, var(--bulma-scheme-main) 40%, transparent);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--bulma-primary);
    opacity: 0.7;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      color-mix(in srgb, var(--bulma-primary) 14%, transparent) 50%,
      transparent 100%
    );
    transform: translateX(-100%);
    animation: item-saving-shimmer 1.6s ease-in-out infinite;
  }
}

@keyframes item-saving-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style>